import { useEffect } from "react";
import { motion } from "framer-motion";
import { Button, Card, Col, Container, Row } from "react-bootstrap";

import aboutMeJSON from "./aboutMeData.json";
import { ExpandableComponent } from "../../components/ExpandableComponent";

interface Education {
  date: string;
  title: string;
  description: string;
  texts: string[];
}

interface WorkExperience {
  date: string;
  jobtitle: string;
  description: string | null;
}

export const AboutMe = () => {
  useEffect(() => {
    document.title = "Mohamad Tarabin - About";
  }, []);

  const educationList: Education[] = aboutMeJSON.education;
  const experienceList: WorkExperience[] = aboutMeJSON.workExperience;

  const isMobile = window.innerWidth < 768;

  const highlightedWordColor = "#7da0ff";

  return (
    <Container>

        {/* About me */}
        {/* About me */}
        {/* About me */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 1.2 }}
        >
          <Row style={{
              marginTop: isMobile ? "100px" : "150px",
              marginBottom: "100px",
              marginLeft: "auto",
              marginRight: "auto",
              justifyContent: "center",
          }}>
            <Col xs={12} lg={10} style={{
              padding: isMobile ? "30px 20px" : "50px 60px",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: "20px",
              background:
                "linear-gradient(145deg, rgba(255,255,255,0.045), rgba(255,255,255,0.015))",
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
            }}>

              {/* Section label */}
              <h2
                style={{
                  color: highlightedWordColor,
                  fontWeight: "bold",
                }}
              >
                ABOUT ME
              </h2>

                  <div
                    style={{
                      borderTop: "1px solid rgba(255,255,255,0.12)",
                      paddingTop: "35px",
                      textAlign: "justify",
                    }}
                  >

                    <p>
                      I'm a dedicated civil engineer with a passion for research and
                      education. I hold a Bachelor's degree in civil engineering from
                      Abu Dhabi University (UAE), a Master’s degree from the University
                      of Sharjah (UAE), and a Ph.D. from McMaster University (Canada).
                      <br/>
                      <br/>

                      I'm presently working as a postdoc at McMaster University, where
                      I supervise graduate student research and conduct my own research
                      on sustainable supplementary cementitious materials and radiation
                      shielding ultra-high-performance concrete. I am also involved in
                      preparing research grants and publishing research in high-impact
                      journals.
                    </p>

                    {/* Motives */}
                      <ExpandableComponent
                        closedStateText="Motives"
                        openedStateText="Hide"
                        buttonVarient="secondary"
                      >
                        <div
                        >
                          I have always been passionate about learning, creating, and
                          developing new ideas. I first explored this passion through
                          programming at the age of 13, teaching myself how to develop
                          software and eventually selling my own programs. Although I
                          enjoyed programming, I chose civil engineering because it
                          allowed me to combine my interest in creating and
                          problem-solving with another field.

                          <br />
                          <br />

                          Throughout my education, I have consistently aimed for
                          excellence, graduating as the top student in my high school
                          and civil engineering department at Abu Dhabi University,
                          with a CGPA of 3.99. I subsequently completed my master's
                          degree at the University of Sharjah and my Ph.D. at McMaster
                          University, achieving a CGPA of 4.0 in both programs.

                          <br />
                          <br />

                          During my studies, I discovered another passion: teaching.
                          Helping my colleagues understand difficult concepts and
                          seeing them succeed made me realize how much I enjoy sharing
                          knowledge. This experience ultimately shaped my interest in
                          pursuing a career that combines engineering, research, and
                          education.
                        </div>
                      </ExpandableComponent>

                  </div>


            </Col>
          </Row>
        </motion.div>


      {/* =====================================================
          EDUCATION
      ===================================================== */}

      <motion.div
        initial={{ opacity: 0, x: 40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <Row
          style={{
            padding: "50px 25px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Col xs={12} lg={4}>
            <h2
              style={{
                fontSize: "clamp(34px, 4vw, 52px)",
                letterSpacing: "-2px",
                fontWeight: 700,
              }}
            >
              Education
            </h2>
          </Col>

          <Col
            xs={12}
            lg={{ span: 7, offset: 1 }}
            style={{ marginTop: "15px" }}
          >
            <div style={{ position: "relative", paddingLeft: "30px" }}>

              {/* Vertical timeline */}
              <div
                style={{
                  position: "absolute",
                  left: "5px",
                  top: "8px",
                  bottom: "8px",
                  width: "1px",
                  backgroundColor: "rgba(255,255,255,0.15)",
                }}
              />

              {educationList.map((education, i) => (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    paddingBottom: "15px",
                    marginBottom: "10px",
                    borderBottom:
                      i === educationList.length - 1
                        ? "none"
                        : "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {/* Timeline circle */}
                  <div
                    style={{
                      position: "absolute",
                      left: "-30px",
                      top: "5px",
                      width: "11px",
                      height: "11px",
                      borderRadius: "50%",
                      backgroundColor: highlightedWordColor,
                      boxShadow: `0 0 10px ${highlightedWordColor}`,
                    }}
                  />

                  <div>
                    {education.date}
                  </div>

                  <h3 style={{ color: highlightedWordColor, }}> {education.title} </h3>

                  <p>{education.description}</p>

                  {education.texts.map((text, j) => (
                    <p key={j}> {text} </p>
                  ))}
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </motion.div>


      {/* =====================================================
          WORK EXPERIENCE
      ===================================================== */}

      <motion.div
        initial={{ opacity: 0, x: -40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <Row
          style={{
            margin: "0 auto",
            padding: "50px 25px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Col xs={12} lg={4}>
            <h2
              style={{
                fontSize: "clamp(34px, 4vw, 52px)",
                letterSpacing: "-2px",
                fontWeight: 700,
              }}
            >
              Work experience
            </h2>
          </Col>

          <Col
            xs={12}
            lg={{ span: 7, offset: 1 }}
            style={{ marginTop: "15px" }}
          >
            <div style={{ position: "relative", paddingLeft: "30px" }}>

              {/* Vertical timeline */}
              <div
                style={{
                  position: "absolute",
                  left: "5px",
                  top: "8px",
                  bottom: "8px",
                  width: "1px",
                  backgroundColor: "rgba(255,255,255,0.15)",
                }}
              />

              {experienceList.map((experience, i) => (
                <div
                  key={i}
                  style={{
                    position: "relative",
                    paddingBottom: "15px",
                    marginBottom: "10px",
                    borderBottom:
                      i === experienceList.length - 1
                        ? "none"
                        : "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {/* Timeline circle */}
                  <div
                    style={{
                      position: "absolute",
                      left: "-30px",
                      top: "5px",
                      width: "11px",
                      height: "11px",
                      borderRadius: "50%",
                      backgroundColor: highlightedWordColor,
                      boxShadow: `0 0 15px ${highlightedWordColor}`,
                    }}
                  />

                  <div> {experience.date} </div>
                  <h3 style={{ color: highlightedWordColor, }}> {experience.jobtitle} </h3>
                  <p> {experience.description} </p>

                </div>
              ))}
            </div>
          </Col>
        </Row>
      </motion.div>


      {/* =====================================================
          SKILLS
      ===================================================== */}

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <Row
          style={{
            margin: "0 auto",
            padding: "50px 25px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Col xs={12} lg={4}>
            <h2 style={{ 
                fontSize: "clamp(34px, 4vw, 52px)",
                letterSpacing: "-2px",
                fontWeight: "bold",
                }} >
              Skills
            </h2>
          </Col>

          <Col
            xs={12}
            lg={{ span: 7, offset: 1 }}
            style={{ marginTop: "15px", }}
          >
            <Row>
              <Col xs={12} md={6}>
                <div>Engineering Software</div>
                <p
                  style={{
                    color: "#8f99aa",
                    marginBottom: "30px",
                  }}
                >
                  AutoCAD, ETABS, Synchro, STAAD Foundation, Slides,
                  Primavera, SAFE, ABAQUS
                </p>
              </Col>
              <Col xs={12} md={6}>
                <div>Microsoft Software</div>
                <p
                  style={{
                    color: "#8f99aa",
                    marginBottom: "30px",
                  }}
                >
                  Word, PowerPoint, Excel, Project
                </p>
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6}>
                <div
                >
                  Programming
                </div>

                <p
                  style={{
                    color: "#8f99aa",
                    marginBottom: "30px",
                  }}
                >
                  Java, C#, Python, HTML, JavaScript, MATLAB, React
                </p>
              </Col>
              <Col xs={12} md={6}>
                <div
                >
                  Professional Skills
                </div>

                <p
                  style={{
                    color: "#8f99aa",
                    marginBottom: "30px",
                  }}
                >
                  Technical writing, oral presentations, time management,
                  critical thinking
                </p>
              </Col>
            </Row>

          </Col>
        </Row>
      </motion.div>


      {/* =====================================================
          CV
      ===================================================== */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1 }}
      >
        <Row
          style={{
            margin: "0 auto",
            padding: "50px 25px 50px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            textAlign: "center",
          }}
        >
          <Col>
            <Button
              href="/files/CV.pdf"
              target="_blank"
              style={{
                padding: "14px 25px",
                border: "none",
                background: "#4d7cff",
                color: "white",
                fontWeight: 600,
              }}
            >
              Download CV <span style={{ marginLeft: "10px" }}>↓</span>
            </Button>
          </Col>
        </Row>
      </motion.div>

    </Container>
  );
};